<template>
  <div class="space-y-6">
    <!-- Outstanding Balance Banner -->
    <div v-if="outstandingBalance !== null && outstandingBalance > 0" class="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-4">
      <p class="font-semibold">Outstanding Prescription Bill</p>
      <p class="font-medium text-red-600">Kes {{ outstandingBalance.toFixed(2) }}</p>
      <p v-if="settleAtEndAllowed" class="text-sm text-green-600 mt-2">
        ✓ Credit billing enabled - Patient can settle bill at the end of visit
      </p>
    </div>

    <table class="w-full border-collapse">
      <thead>
        <tr class="bg-gray-100">
          <th class="p-2 text-left">Drug</th>
          <th class="p-2 text-left">Dosage</th>
          <th class="p-2 text-left">Frequency</th>
          <th class="p-2 text-left">Duration</th>
          <th class="p-2 text-left">Quantity</th>
          <th class="p-2 text-left">Route</th>
          <th class="p-2 text-left">Status</th>
          <th class="p-2 text-left">Notes</th>
          <th class="p-2 text-left">Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(medication, index) in approvedPrescriptions" :key="index">
          <td class="p-2 border">
            {{ medication.inventory_item?.trade_name }}
          </td>
          <td class="p-2 border">
            {{ medication.dose }}
            {{ medication.inventory_item?.drug_strength_unit?.name }}
          </td>
          <td class="p-2 border">
            {{ medication.inventory_dosage_frequency?.name }}
          </td>
          <td class="p-2 border">{{ medication.days }} day(s)</td>
          <td class="p-2 border">{{ medication.quantity }}</td>
          <td class="p-2 border">
            {{ medication.administration_route?.name }}
          </td>
          <td class="p-2 border">{{ medication.status?.name }}</td>
          <td class="p-2 border">{{ medication.notes }}</td>
          <td class="p-2 border">
            <button
              v-if="medication.status?.code === 'ST-016'"
              @click="printLabel(index)"
              class="px-2 py-1 bg-orange-400 text-white rounded-md hover:bg-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-opacity-50 text-sm"
            >
              Print Label
            </button>
            <select
              v-if="medication.status?.code === 'ST-018'"
              id="batch"
              v-model="batches[medication.id]"
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-custom-yellow focus:ring focus:ring-custom-yellow focus:ring-opacity-50 bg-gray-50"
            >
              <option :value="null" :selected="true">Select batch</option>
              <option
                v-for="batch of allActiveBatches(medication)"
                :key="batch.id"
                :value="batch"
              >
                Batch: {{ batch.batch_no }} | Expires: {{ batch.expiry_date }} | Avl. Qty: {{ batch.available_quantity }} | KES {{ batch.selling_price }}
              </option>
            </select>
            <!-- Warn if no active batches available -->
            <p
              v-if="medication.status?.code === 'ST-018' && allActiveBatches(medication).length === 0"
              class="text-xs text-red-500 mt-1"
            >
              No active batches available
            </p>
          </td>
        </tr>
      </tbody>
    </table>

    <div class="flex justify-end space-x-4">
      <button
        @click="cancel"
        class="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50"
      >
        Cancel
      </button>
      <button
        v-if="pendingBillingPrescriptions.length"
        @click="addToBill"
        :disabled="isLoading || selectedForBilling.length === 0"
        class="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        <span v-if="isLoading">Adding to Bill...</span>
        <span v-else-if="selectedForBilling.length === 0">Select a batch to add drugs to bill</span>
        <span v-else>Add {{ selectedForBilling.length }} Selected Drug{{ selectedForBilling.length > 1 ? 's' : '' }} to Bill</span>
      </button>
      <button
        v-if="readyToIssuePrescriptions.length"
        @click="issueMedication"
        :disabled="isLoading"
        class="px-4 py-2 bg-orange-400 text-white rounded-md hover:bg-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:ring-opacity-50"
      >
        {{ isLoading ? "Validating & Issuing..." : "Issue" }}
      </button>
    </div>

    <div
      id="printableLabel"
      class="bg-white hidden border-t-8 border-b-8 border-orange-400"
    >
      <div class="flex justify-center items-start p-2">
        <div class="w-1/3 pr-2">
          <h3 class="font-bold text-blue-800 mb-2">WARNINGS</h3>
          <ul class="list-none pl-0 text-sm space-y-1">
            <li>Avoid alcohol beverages</li>
            <li>May cause dizziness</li>
            <li>Take this after meals</li>
          </ul>
        </div>
        <div
          class="w-1/3 text-sm space-y-2 px-2 border-l border-r border-gray-200"
        >
          <div>
            <p class="font-semibold">Patient Name:</p>
            <p>{{ prescriptionDetails.patient.FullName }}</p>
          </div>
          <div>
            <p class="font-semibold">Medication:</p>
            <p>{{ currentMedication.trade_name }} {{ currentMedication.dose }}</p>
          </div>
          <div>
            <p class="font-semibold">Directions:</p>
            <p>{{ currentMedication.directions }}</p>
          </div>
          <div>
            <p class="font-semibold">Quantity:</p>
            <p>{{ currentMedication.quantity }}</p>
          </div>
          <div>
            <p class="font-semibold">Date Issued:</p>
            <p>{{ currentMedication.dateIssued }}</p>
          </div>
          <div>
            <p class="font-semibold">Refills:</p>
            <p>{{ currentMedication.refills || 0 }} Refills</p>
          </div>
        </div>
        <div class="w-1/3 flex justify-end" style="transform: rotate(-90deg)">
          <svg id="barcode"></svg>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import apiClient from "@/services/api";
import JsBarcode from "jsbarcode";

export default {
  name: "AllocatePrescriptionTab",
  props: {
    prescriptionDetails: {
      type: Object,
      required: true,
      isEditing: Boolean,
    },
  },
  data() {
    return {
      currentMedication: {},
      batches: {},
      isLoading: false,
      outstandingBalance: null,
      settleAtEndAllowed: false,
    };
  },
  computed: {
    approvedPrescriptions() {
      return this.prescriptionDetails.prescriptions.filter(
        (prescription) => prescription.status.code === "ST-018" || prescription.status.code === "ST-016"
      );
    },
    /**
     * FIX: Approved (ST-018) drugs that have NOT been billed yet — these need
     * "Add to Bill". Split from readyToIssuePrescriptions so a mixed set (some
     * already billed, some not — e.g. after a pharmacist issues 2 of 3 drugs in
     * one session and comes back later for the 3rd) shows the right button for
     * each drug's own state, instead of one global flag hiding "Add to Bill"
     * just because a sibling drug was already billed.
     */
    pendingBillingPrescriptions() {
      return this.approvedPrescriptions.filter(
        (p) => p.status.code === "ST-018" && !p.bill_item_id
      );
    },
    /**
     * FIX: Approved (ST-018) drugs that ARE already billed — the only ones
     * safe to pass to Issue or to a coverage/balance check.
     */
    readyToIssuePrescriptions() {
      return this.approvedPrescriptions.filter(
        (p) => p.status.code === "ST-018" && p.bill_item_id
      );
    },
    hasBillItems() {
      return this.readyToIssuePrescriptions.length > 0;
    },
    /**
     * FIX: "Add to Bill" is now opt-in per drug — the pharmacist picks a batch
     * only for the drugs the patient actually wants right now. Previously this
     * required a batch for EVERY approved-but-unbilled drug before the button
     * would even enable, forcing an all-or-nothing choice when a patient might
     * only want 2 of 3 approved drugs. Any drug left without a batch simply
     * stays on this tab, still approved, ready to be billed later.
     */
    selectedForBilling() {
      return this.pendingBillingPrescriptions.filter((p) => this.batches[p.id] != null);
    },
  },
  mounted() {
    this.checkBillItemsAndBalance();
  },
  watch: {
    'prescriptionDetails.prescriptions': {
      handler() {
        this.checkBillItemsAndBalance();
      },
      deep: true,
    },
  },
  methods: {
    /**
     * Flatten active (non-expired) batches across ALL store items for a medication.
     * Replaces the old hardcoded [0].inventory_store_stock_items approach which:
     *   - Only checked the first store item
     *   - Included expired batches (backend now filters these via active_stock_items scope)
     */
    allActiveBatches(medication) {
      return (medication.inventory_item?.inventory_store_items || [])
        .flatMap((si) => si.active_stock_items || []);
    },

    /**
     * FIX: This used to call addPrescriptionsToBill — a MUTATING endpoint —
     * just to "check" a balance. It required a batch_id per prescription and
     * would try to bill drugs not ready to be billed yet, failing with "Batch
     * not found" the moment you landed on this tab with a mixed set. Switched
     * to verify_pharmacy_coverage: read-only, and only ever looks at drugs
     * that already have a bill_item_id — exactly what we want a balance for.
     */
    async checkBillItemsAndBalance() {
      if (this.readyToIssuePrescriptions.length === 0) {
        this.outstandingBalance = null;
        return;
      }
      try {
        const payload = {
          prescriptions: this.readyToIssuePrescriptions.map((medication) => ({ id: medication.id })),
          userId: this.$store.state.userId,
        };
        const response = await apiClient.verifyPharmacyCoverage(payload);
        if (response.success) {
          this.outstandingBalance = response.data.remaining_balance;
          this.settleAtEndAllowed = response.data.settle_at_end_allowed;
        } else {
          this.$toast.error(response.message || "Failed to fetch outstanding balance.");
        }
      } catch (error) {
        this.$toast.error("Failed to fetch outstanding balance.");
      }
    },

    /**
     * Add prescriptions to bill.
     * FIX: only bills the drugs the pharmacist selected a batch for
     * (selectedForBilling), not every approved-but-unbilled drug — a patient
     * can choose to take only some of what was prescribed. batch_id is
     * included per prescription so the backend prices the bill from the
     * selected batch's selling_price — no silent auto-selection.
     */
    async addToBill() {
      this.isLoading = true;
      try {
        const payload = {
          prescriptions: this.selectedForBilling
            .map((medication) => ({
              id: medication.id,
              batch_id: this.batches[medication.id].id,
            })),
          userId: this.$store.state.userId,
        };
        const response = await apiClient.addPrescriptionsToBill(payload);
        if (response.success) {
          this.outstandingBalance = response.data.outstanding_balance;
          this.$toast.success("Prescriptions added to bill successfully.");
          await this.$emit("refetch");
        } else {
          this.$toast.error(response.message || "Failed to add prescriptions to bill.");
        }
      } catch (error) {
        this.$toast.error("Failed to add prescriptions to bill.");
      } finally {
        this.isLoading = false;
      }
    },

    async issueMedication() {
      let payload = {};
      try {
        payload = { prescriptions: this.preparePayload() };
      } catch (error) {
        return;
      }

      this.isLoading = true;
      try {
        const coverageResponse = await apiClient.verifyPharmacyCoverage(payload);

        if (!coverageResponse.success) {
          this.$toast.error(coverageResponse.message || "Failed to verify coverage.");
          this.isLoading = false;
          return;
        }

        const {
          is_covered,
          requires_cash_payment,
          remaining_balance,
          settle_at_end_allowed
        } = coverageResponse.data;

        this.outstandingBalance = remaining_balance;
        this.settleAtEndAllowed = settle_at_end_allowed;

        if (settle_at_end_allowed === true) {
          if (!is_covered && requires_cash_payment && remaining_balance > 0) {
            this.$toast.info(
              `Credit billing: Patient can collect prescriptions and settle KES ${remaining_balance.toFixed(2)} at the end of visit.`,
              { duration: 5000 }
            );
          }
          await this.proceedWithIssuing(payload);

        } else if (is_covered === true) {
          await this.proceedWithIssuing(payload);

        } else if (requires_cash_payment === true) {
          this.$toast.error(
            `Payment required: Please settle KES ${remaining_balance.toFixed(2)} before issuing prescriptions.`,
            { duration: 5000 }
          );
          this.isLoading = false;
          return;

        } else {
          await this.proceedWithIssuing(payload);
        }

      } catch (error) {
        console.error('Error in issueMedication:', error);
        this.$toast.error("Failed to issue prescriptions.");
        this.isLoading = false;
      }
    },

    async proceedWithIssuing(payload) {
      try {
        const response = await apiClient.issuePharmacyPrescription(payload);
        if (response.success) {
          this.isLoading = false;
          this.$emit("close");
          this.$emit("fetch-prescriptions");
          this.$toast.success("Prescriptions issued successfully.");
        } else {
          this.$toast.error(response.message || "Failed to issue prescriptions.");
          this.isLoading = false;
        }
      } catch (error) {
        console.error('Error issuing prescriptions:', error);
        this.$toast.error("Failed to issue prescriptions.");
        this.isLoading = false;
      }
    },

    preparePayload() {
      return this.readyToIssuePrescriptions
        .map((medication) => {
          const selectedBatch = this.batches[medication.id];
          if (selectedBatch === null || selectedBatch === undefined) {
            this.$toast.error("Please select batches for all prescriptions.");
            throw new Error("Batch selection required");
          }
          return {
            id: medication.id,
            batch_id: selectedBatch.id,
          };
        });
    },

    cancel() {
      this.$emit("close");
    },

    /**
     * FIX: This used to just console.log("Printing label...") — no label was
     * ever actually rendered or printed. The #printableLabel markup and the
     * jsbarcode dependency both already existed in this file (and an
     * abandoned prototype at src/views/labels/PrescriptionLabel.vue showed
     * the intended JsBarcode call), they just weren't wired together. Also
     * fixed prescriptionDetails.created_at, which was always blank — Visits
     * have no timestamp columns at all — by using the actual print moment
     * (dispensing date) instead, which is what a dispensing label should show.
     */
    printLabel(index) {
      const medication = this.approvedPrescriptions[index];
      if (!medication) return;

      const frequency = medication.inventory_dosage_frequency?.name;
      const route = medication.administration_route?.name;
      const directions = [frequency, route, medication.days ? `for ${medication.days} day(s)` : null]
        .filter(Boolean)
        .join(", ");

      this.currentMedication = {
        trade_name: medication.inventory_item?.trade_name || "-",
        dose: medication.dose
          ? `${medication.dose} ${medication.inventory_item?.drug_strength_unit?.name || ""}`.trim()
          : "",
        directions: directions || "-",
        quantity: medication.quantity || "-",
        refills: medication.refills || 0,
        dateIssued: new Date().toLocaleDateString("en-GB"),
      };

      this.$nextTick(() => {
        try {
          JsBarcode("#barcode", medication.id || "N/A", {
            format: "CODE128",
            width: 2,
            height: 60,
            displayValue: false,
          });
        } catch (error) {
          console.error("Failed to generate label barcode:", error);
        }

        const printStyle = document.createElement("style");
        printStyle.id = "pharmacy-label-print-style";
        printStyle.textContent = `
          @media print {
            body * { visibility: hidden; }
            #printableLabel, #printableLabel * { visibility: visible; }
            #printableLabel {
              display: block !important;
              position: fixed;
              top: 0;
              left: 0;
              width: 100%;
            }
          }
        `;
        document.head.appendChild(printStyle);

        window.print();

        document.head.removeChild(printStyle);
      });
    },
  },
};
</script>

<style scoped>
#printableLabel {
  border-top-color: #f9a03f;
  border-bottom-color: #f9a03f;
}

h3 {
  color: #00008b;
}

.font-semibold {
  color: #000000;
}

p:not(.font-semibold) {
  color: #333333;
}

ul {
  color: #333333;
}
</style>
